import { useMemo, useState } from 'react';
import { Scale, X } from 'lucide-react';
import type { ProductoVista } from '@shared/types';
import { aCantidadBase, decirCantidad, esUnidadContinua, unidadDeCaptura } from '@shared/medidas';
import { formatPrecio } from '@renderer/utils/formatters';

/**
 * Sprint 131 — *"Quiero un sirloin de 350 gramos, no me importa el precio"*.
 *
 * El dueño lo predijo cuando le preguntamos a la dueña si pesa el corte al momento:
 *
 * > *"Hoy dirá que no… y un día menos que otro dirá: **¿cómo puedo hacer una venta por peso
 * > libre?** (porque un cliente importante o un amigo distinguido quiere…)"*
 *
 * Los tamaños fijos alcanzan hasta que llega el cliente al que no le alcanzan, y ese cliente
 * siempre llega. El Sprint 127 hizo que cualquier cantidad **quepa en el catálogo**; esto la hace
 * vendible **sin dar de alta nada**.
 *
 * ## Por qué está aquí y no en la rejilla del menú
 *
 * Porque es el caso raro. Tocar un producto sigue siendo un toque, y la pantalla del caso común
 * —la que se usa cien veces al día— no cambia. Meter un segundo botón en cada tarjeta del menú
 * habría hecho más lenta la venta normal para servir a la excepción.
 *
 * ## El precio se calcula; no se teclea
 *
 * Sale del precio por kilo del tamaño de referencia y **lo vuelve a calcular el servidor**. Un
 * campo de precio libre en el punto de venta es la puerta para cobrar lo que uno quiera, y se
 * abriría para los cuatro oficios, no sólo para la dueña. Si el negocio quiere hacer un precio
 * especial, eso es un descuento y tiene su propio camino, con motivo y registro.
 */
export function VenderAlPesoModal({
  abierto,
  productos,
  monedaSimbolo,
  locale,
  onCerrar,
  onAgregar,
}: {
  abierto: boolean;
  productos: ProductoVista[];
  monedaSimbolo: string;
  locale: string;
  onCerrar: () => void;
  onAgregar: (datos: {
    producto: ProductoVista;
    cantidadBase: number;
    precio: number;
    nombre: string;
  }) => void;
}) {
  const [productoId, setProductoId] = useState<number | null>(null);
  const [texto, setTexto] = useState('');

  /**
   * Lo que se puede pesar: un renglón por ingrediente, con el tamaño **más grande** como
   * referencia de precio.
   *
   * Es el mismo criterio que usa la tabla de formatos del catálogo (Sprint 127): el tamaño grande
   * es el que el negocio fija primero y del que cuelgan los demás. Si aquí se tomara otro, el
   * precio por kilo cambiaría según qué renglón se hubiera elegido, que es exactamente el tipo de
   * número que nadie puede explicar después.
   */
  const pesables = useMemo(() => {
    const porInsumo = new Map<number, ProductoVista>();
    for (const p of productos) {
      if (!p.habilitado) continue;
      if (!p.insumoId || !p.gramaje || p.gramaje <= 0) continue;
      if (!esUnidadContinua(p.unidadVenta ?? '')) continue;
      const previo = porInsumo.get(p.insumoId);
      if (!previo || (previo.gramaje ?? 0) < p.gramaje) porInsumo.set(p.insumoId, p);
    }
    return [...porInsumo.values()].sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [productos]);

  const producto = pesables.find((p) => p.id === productoId) ?? null;
  const unidad = producto?.unidadVenta ?? 'kg';
  const captura = unidadDeCaptura(unidad);

  const cantidadBase = aCantidadBase(Number(texto.replace(',', '.')) || 0, unidad);
  const precioPorUnidad = producto && producto.gramaje ? producto.precio / producto.gramaje : 0;
  const precio = Math.round(precioPorUnidad * cantidadBase);
  // El nombre que va a quedar en el ticket y en la comanda; el servidor arma el suyo igual.
  const nombreFinal = producto ? `${nombreBase(producto)} ${decirCantidad(cantidadBase, unidad)}` : '';

  if (!abierto) return null;

  const puedeAgregar = producto !== null && cantidadBase > 0 && precio > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-black/50 p-4">
      <div className="card my-4 w-full max-w-lg">
        <div className="mb-4 flex items-start justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 text-xl font-bold">
              <Scale className="h-5 w-5" />
              Vender al peso
            </h2>
            <p className="text-sm text-slate-600">
              Para cuando el cliente pide una cantidad que no está en tu lista.
            </p>
          </div>
          <button onClick={onCerrar} className="btn-ghost p-2" aria-label="Cerrar">
            <X className="h-4 w-4" />
          </button>
        </div>

        {pesables.length === 0 ? (
          /* La pantalla vacía enseña: dice por qué está vacía y qué hacer. */
          <p className="rounded-lg bg-slate-50 px-3 py-3 text-sm text-slate-700">
            Todavía no tienes nada que se venda por kilo o por litro. Cuando des de alta algo que
            se pesa, aparecerá aquí.
          </p>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="label" htmlFor="pesoProducto">
                ¿Qué le vas a pesar?
              </label>
              <select
                id="pesoProducto"
                className="input"
                value={productoId ?? ''}
                onChange={(e) => setProductoId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">— Elige —</option>
                {pesables.map((p) => (
                  <option key={p.id} value={p.id}>
                    {nombreBase(p)}
                  </option>
                ))}
              </select>
            </div>

            {producto && (
              <>
                <div>
                  <label className="label" htmlFor="pesoCantidad">
                    ¿Cuántos {captura.nombre}?
                  </label>
                  <input
                    id="pesoCantidad"
                    className="input font-mono text-2xl"
                    inputMode="decimal"
                    value={texto}
                    onChange={(e) => setTexto(e.target.value)}
                    placeholder="350"
                    autoFocus
                  />
                  <p className="mt-1 text-xs text-slate-600">
                    Sale a {formatPrecio(Math.round(precioPorUnidad), monedaSimbolo, locale)} por{' '}
                    {unidad}.
                  </p>
                </div>

                {/* Lo que va a quedar, antes de que quede. El sistema explica su número. */}
                {cantidadBase > 0 && (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-3">
                    <div className="text-sm text-emerald-900">{nombreFinal}</div>
                    <div className="font-mono text-2xl font-bold text-emerald-800">
                      {formatPrecio(precio, monedaSimbolo, locale)}
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="flex justify-end gap-2">
              <button onClick={onCerrar} className="btn-ghost">
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (!producto) return;
                  onAgregar({ producto, cantidadBase, precio, nombre: nombreFinal });
                  setTexto('');
                  setProductoId(null);
                }}
                className="btn-primary min-h-[44px]"
                disabled={!puedeAgregar}
              >
                Agregar al pedido
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * El nombre sin el tamaño: "Sirloin 1 kg" → "Sirloin".
 *
 * El producto de referencia se llama como su formato, y eso en esta pantalla confunde: no se está
 * vendiendo el kilo, se está pesando el sirloin.
 */
function nombreBase(p: ProductoVista): string {
  return p.nombre.replace(/\s+\d+([.,]\d+)?\s*(kg|kilos?|g|gr|gramos?|l|lt|litros?|ml)\b.*$/i, '')
    .replace(/\s+\d+\/\d+\s*\w*\s*$/i, '')
    .trim() || p.nombre;
}
